/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from 'react-intl';
import * as AppPropTypes from '../../lib/PropTypes';

import Link from '../partials/Link';
import Image from '../partials/Image';
import LinkButton from '../buttons/LinkButton';
import { useIntersectionObserver } from '../../hooks/useObserver';

import styles from '../../../styles/items/project-tile.module.scss';

const propTypes = {
    item: AppPropTypes.project.isRequired,
    withoutButtonLabel: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    withoutButtonLabel: false,
    className: null,
};

const ProjectTile = ({ item, withoutButtonLabel, className }) => {
    const [hover, setHover] = useState(false);

    const {
        ref,
        entry: { isIntersecting = false },
    } = useIntersectionObserver({ rootMargin: '-10%' });

    const {
        title = null,
        url = null,
        logo = null,
        image = null,
        plainImage = null,
        cardImage = null,
        theme = null,
        broadcasters = null,
        clients = null,
        categories = null,
        cardCategoryLabel = null,
    } = item || {};

    const bottomLabel = useMemo(() => {
        const projectCategories = categories !== null ? categories || [] : [];
        const projectBroadcasters = broadcasters !== null ? broadcasters || [] : [];
        const projectClients = clients !== null ? clients || [] : [];

        const { label: category = '' } = projectCategories.length > 0 ? projectCategories[0] : {};
        const { name: broadcaster = '' } =
            projectBroadcasters.length > 0 ? projectBroadcasters[0] : {};
        const { name: client = '' } = projectClients.length > 0 ? projectClients[0] : {};

        const prefix = broadcaster || client || null;
        const suffix = cardCategoryLabel || category || null;

        if (prefix !== null && suffix !== null) {
            return `${prefix}${prefix !== null && suffix !== null ? ' — ' : ''}${suffix}`;
        }
        if (prefix !== null) {
            return prefix;
        }
        return suffix || '';
    }, [categories, broadcasters, clients]);

    const colors = useMemo(
        () =>
            theme !== null && theme.colors !== null
                ? theme.colors
                : { primary: '#222222', secondary: '#0a0808' },
        [theme],
    );

    const tileValue = hover ? -3 : 0;
    const coverValue = hover ? 2 : 0;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            ref={ref}
            style={{
                opacity: `${isIntersecting ? 1 : 0}`,
                // color: elementsColor,
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <Link
                href={url}
                className={styles.tile}
                style={{
                    backgroundImage: `linear-gradient(to bottom, ${colors.secondary} 0%, ${colors.primary} 100%)`,
                    transform: `translateX(${isIntersecting ? tileValue : -30}px)`,
                }}
            >
                <div
                    className={styles.cover}
                    style={{
                        transform: `translate(${isIntersecting ? coverValue * 2 : 60}px, ${
                            isIntersecting ? coverValue : 60
                        }px)`,
                    }}
                >
                    <Image className={styles.background} media={cardImage || plainImage || image} />
                    {logo !== null && cardImage === null && plainImage !== null ? (
                        <div className={styles.logo}>
                            <Image
                                className={styles.logoImg}
                                media={logo}
                                alt={title}
                                size="medium"
                            />
                        </div>
                    ) : null}
                </div>
                <LinkButton className={styles.viewProjectLink}>
                    {!withoutButtonLabel ? (
                        <FormattedMessage
                            defaultMessage="Go to project"
                            description="Project link label"
                        />
                    ) : null}
                </LinkButton>
            </Link>
            <div
                className={styles.content}
                style={{
                    transform: `translateY(${isIntersecting ? 0 : 1}rem)`,
                    opacity: isIntersecting ? 1 : 0,
                }}
            >
                <h2
                    className={classNames([
                        styles.title,
                        {
                            [styles.long]: title.length > 20,
                        },
                    ])}
                >
                    {title}
                </h2>
            </div>
            <div
                className={styles.infos}
                style={{
                    transform: `translateY(${isIntersecting ? 0 : 1}rem)`,
                    opacity: isIntersecting ? 1 : 0,
                }}
            >
                <h2 className={classNames([styles.title])}>{bottomLabel}</h2>
            </div>
        </div>
    );
};

ProjectTile.propTypes = propTypes;
ProjectTile.defaultProps = defaultProps;

export default ProjectTile;
